import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useI18nContext } from "~/i18n/i18n-react";
import useAuthStore from "~/stores/auth";
import { api } from "~/utils/api";
import { resolveSttDomain, toSttProvider } from "~/utils/stt-models";
import { TRACK_EVENTS } from "../../app-insights/event-tracking/types";
import useTrackEvent from "../../app-insights/event-tracking/use-track-event";
import { Button } from "../../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../ui/dialog";
import { Form } from "../../ui/form";
import {
	type CreateRoomFormValues,
	createRoomSchema,
	getCreateRoomDefaultValues,
	ROOM_DETAILS_FIELDS,
} from "./create-room-schema";
import { RoomDetailsStep } from "./room-details-step";
import { RoomModelStep } from "./room-model-step";
import { StepIndicator } from "./step-indicator";

const DETAILS_STEP = 0;
const MODEL_STEP = 1;

export const CreateRoomModal = ({ children }: PropsWithChildren) => {
	const user = useAuthStore((state) => state.user);
	const trackCreateRoom = useTrackEvent({
		eventName: TRACK_EVENTS.CREATE_ROOM,
	});
	const { LL } = useI18nContext();

	const [open, setOpen] = useState(false);
	const [step, setStep] = useState<typeof DETAILS_STEP | typeof MODEL_STEP>(
		DETAILS_STEP
	);
	const { mutate, isPending } = api.room.create.useMutation();
	const { room } = api.useUtils();

	const form = useForm<CreateRoomFormValues>({
		resolver: zodResolver(createRoomSchema),
		mode: "onChange",
		defaultValues: getCreateRoomDefaultValues(),
	});

	const stepLabels = [
		LL.CREATE_ROOM_FORM.STEP_DETAILS(),
		LL.CREATE_ROOM_FORM.STEP_MODEL(),
	];

	const resetWizard = () => {
		form.reset(getCreateRoomDefaultValues());
		setStep(DETAILS_STEP);
	};

	const handleOpenChange = (nextOpen: boolean) => {
		setOpen(nextOpen);
		// Discard a half-finished draft so reopening never resumes mid-wizard.
		if (!nextOpen) {
			resetWizard();
		}
	};

	const handleContinue = async () => {
		const isDetailsValid = await form.trigger(ROOM_DETAILS_FIELDS);
		if (isDetailsValid) {
			setStep(MODEL_STEP);
		}
	};

	const handleSubmit = (data: CreateRoomFormValues) => {
		const sttProvider = toSttProvider(data.model);
		const submitData = {
			language: data.language === "auto" ? undefined : data.language,
			userId: user?.id,
			name: data.name.trim(),
			startTime: data.startTime,
			endTime: data.endTime,
			sttProvider,
			// A domain only reaches the room when the chosen model can use one.
			sttDomain: resolveSttDomain(sttProvider, data.sttDomain),
			modelLocked: data.modelLocked,
		};

		trackCreateRoom({ status: "creating", ...submitData });

		mutate(submitData, {
			onSettled: (result, error) => {
				if (error) {
					trackCreateRoom({
						status: "error",
						error: error.message,
						...submitData,
					});
					toast.error(LL.SOMETHING_WENT_WRONG(), {
						description: error.message,
					});
				} else if (result) {
					trackCreateRoom({ status: "created", ...result, ...submitData });
					toast(LL.CREATE_ROOM_TOAST.TITLE(), {
						description: LL.CREATE_ROOM_TOAST.DESCRIPTION(),
					});
					room.invalidate();
					resetWizard();
					setOpen(false);
				}
			},
		});
	};

	const isDetailsStep = step === DETAILS_STEP;

	return (
		<Dialog onOpenChange={handleOpenChange} open={open}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="overflow-auto" dir="auto">
				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)}>
						<DialogHeader>
							<DialogTitle>{LL.CREATE_ROOM()}</DialogTitle>
							<DialogDescription>
								{isDetailsStep
									? LL.CREATE_ROOM_FORM.DESCRIPTION()
									: LL.CREATE_ROOM_FORM.MODEL_DESCRIPTION()}
							</DialogDescription>
						</DialogHeader>

						<div className="py-4">
							<StepIndicator currentStep={step} steps={stepLabels} />
						</div>

						{/* Min height keeps the dialog from resizing between steps */}
						<div className="min-h-[22rem] pb-4">
							{isDetailsStep ? (
								<RoomDetailsStep form={form} />
							) : (
								<RoomModelStep form={form} />
							)}
						</div>

						<DialogFooter>
							{isDetailsStep ? (
								<Button onClick={handleContinue} type="button">
									{LL.CREATE_ROOM_FORM.CONTINUE()}
									<ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
								</Button>
							) : (
								<>
									<Button
										disabled={isPending}
										onClick={() => setStep(DETAILS_STEP)}
										type="button"
										variant="ghost"
									>
										<ArrowLeft className="me-2 h-4 w-4 rtl:rotate-180" />
										{LL.CREATE_ROOM_FORM.BACK()}
									</Button>
									<Button
										disabled={isPending || !form.formState.isValid}
										type="submit"
									>
										{isPending && (
											<Loader2 className="me-2 h-4 w-4 animate-spin" />
										)}
										{LL.CREATE_ROOM()}
									</Button>
								</>
							)}
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
